import { apiFetch } from './api';

/** Conversation fields returned by the Phase 13 inbox read API. */
export interface ConversationInboxItem {
  id: string;
  account_id: string;
  igsid: string;
  username: string | null;
  automation_paused: boolean;
  paused_at: number | null;
  last_message_at: number | null;
  last_message_preview: string | null;
  unread: boolean;
  created_at: number;
  updated_at: number;
}

/** Message fields returned by the Phase 13 messages read API. */
export interface ConversationMessageItem {
  id: string;
  conversation_id: string;
  direction: 'inbound' | 'outbound';
  kind: 'text' | 'media' | 'story_reply' | 'mention' | 'echo' | 'system';
  text: string | null;
  attachments_json: string | null;
  external_timestamp: number | null;
  created_at: number;
}

export interface ConversationListResponse {
  conversations: ConversationInboxItem[];
  limit: number;
  offset: number;
  total: number;
}

export interface ConversationDetailResponse {
  conversation: ConversationInboxItem;
}

export interface ConversationMessagesResponse {
  messages: ConversationMessageItem[];
  limit: number;
  offset: number;
  total: number;
}

/**
 * Response of the Phase 12 automation endpoints (pause/resume). The backend
 * is authoritative: these responses carry the current `automation_paused`
 * state directly, so the UI never infers state from the request it sent.
 */
export interface AutomationActionResponse {
  conversation: { id: string; account_id: string; igsid: string };
  automation_paused: boolean;
  paused_at: number | null;
}

/**
 * The Phase 12 routes key on the composite `<accountId>:<igsid>` pair (the
 * conversations table's UNIQUE key), not the Phase 13 primary key. Both
 * parts come from an existing conversation response — never from user
 * input — and are encoded individually so the colon stays the separator.
 */
function compositeConversationId(accountId: string, igsid: string): string {
  return `${encodeURIComponent(accountId)}:${encodeURIComponent(igsid)}`;
}

export function pauseConversationRequest(
  accountId: string,
  igsid: string,
): Promise<AutomationActionResponse> {
  return apiFetch<AutomationActionResponse>(
    `/api/conversations/${compositeConversationId(accountId, igsid)}/pause`,
    { method: 'POST' },
  );
}

export function resumeConversationRequest(
  accountId: string,
  igsid: string,
): Promise<AutomationActionResponse> {
  return apiFetch<AutomationActionResponse>(
    `/api/conversations/${compositeConversationId(accountId, igsid)}/resume`,
    { method: 'POST' },
  );
}

export function listConversationsRequest(
  limit: number,
  offset: number,
): Promise<ConversationListResponse> {
  return apiFetch<ConversationListResponse>(`/api/conversations?limit=${limit}&offset=${offset}`);
}

export function getConversationRequest(id: string): Promise<ConversationDetailResponse> {
  return apiFetch<ConversationDetailResponse>(`/api/conversations/${encodeURIComponent(id)}`);
}

export function listMessagesRequest(
  id: string,
  limit: number,
  offset: number,
): Promise<ConversationMessagesResponse> {
  return apiFetch<ConversationMessagesResponse>(
    `/api/conversations/${encodeURIComponent(id)}/messages?limit=${limit}&offset=${offset}`,
  );
}
